import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'
import {
  DocumentListCard,
  Unifree,
  ErrorPage503,
  useDisplayUserError,
  Text
} from '@yes.technology/react-toolkit'

import { GridContainer } from 'style/grid'
import * as interactionApi from '../utils/api'
import { interactionModelOperations } from '../redux'
import InteractionStep from '../components/InteractionStep'
import ScrollToTop from '../../shared/ScrollToTop'
import Success from '../components/Success'
import {
  BlueprintsContext,
  useInteractionModel,
  useGeolocation,
  useInitialConfig,
  useStep,
  useNavigation,
  useBlueprintsAfterExecution,
  useDraftInteraction,
  useValuesFromProcess,
  useBaseInteraction
} from '../hooks'
import moment from 'moment'
import InteractionInformation from '../components/InteractionInformation'
import { persistor, useAppDispatch, useAppSelector } from 'state/store'
import Loading from 'modules/shared/Loading'
import {
  StickyArea,
  StyledNavigationSlider
} from './InteractionContainer.styles'
import ReviewSaveButton from '../components/Review/components/ReviewSaveButton/ReviewSaveButton'
import { ReviewSaveButtonContextProvider } from '../components/Review/components/ReviewSaveButton/ReviewSaveButtonContext'
import { SelectAndLinkFrameContextProvider } from '../components/SelectAndLinkFrame/SelectAndLinkFrameContext'
import { Activity } from 'types/shared'
import formatBlueprintsResponseActivities from 'utils/formatBlueprintsResponseActivities'
import formatBlueprintsRequestActivities from 'utils/formatBlueprintsRequestActivities'
import { useLocation } from 'react-router-dom'
import { flatValuesToOldActivities } from '../utils/flatValuesToOldActivities'
import { BlueprintTrigger } from '../hooks/useBlueprintsContext'
import { AllowedLocales } from 'utils/date'
import GenericErrorPage from '../components/GenericErrorPage'
import {
  DraftNotFound as DraftNotFoundErrorPage,
  DraftIsConcluded as DraftIsConcludedErrorPage
} from '../components/DraftErrorPages'

const InteractionContainer = () => {
  const { t, language } = useTranslation()

  const { search } = useLocation()

  const searchParams = new URLSearchParams(search)
  const hideInteractionContainer =
    searchParams.has('hideInteractionContainer') ?? false

  const { displayUserError } = useDisplayUserError()

  const [isFetchingBlueprints, setIsFetchingBlueprints] = useState(false)
  const [isFirstBlueprintsRequest, setIsFirstBlueprintsRequest] = useState(true)

  const dispatch = useAppDispatch()

  const apiState = useAppSelector((state) => state.api)

  const blueprintsPromise = useRef<Promise<void> | null>(null)
  const shouldExecuteInitialBlueprint = useRef(true)
  const shouldAutofillActivities = useRef(true)

  const {
    isFetchingInteractionModel,
    interactionModel,
    interactionModelError
  } = useInteractionModel({ currentTime: moment().format() })

  const workflowUuid = interactionModel.workflowUuid
  const interactionModelData = interactionModel.flatData

  const draftInteractionUuid = searchParams.get('draft_interaction_uuid') || ''

  const { draftInteraction, isFetched: isDraftFetched } = useDraftInteraction({
    interactionUuid: draftInteractionUuid,
    interactionModelUuid: interactionModelData?.uuid
  })

  const { hasAppliedValuesFromProcess } = useValuesFromProcess({
    enabled: !draftInteractionUuid
  })

  useBaseInteraction({ enabled: !draftInteractionUuid })

  useGeolocation(interactionModel.geolocation)
  useInitialConfig(interactionModel)

  const clearReduxPersist = () => {
    persistor.pause()
    persistor.flush().then(() => persistor.purge())
  }

  const {
    steps,
    totalSteps,
    currentStepIndex,
    changeStep,
    isLoadedSteps,
    isInteractionFinished,
    hasPendingDecisionModels
  } = useStep(interactionModel, clearReduxPersist)

  const showTextForTotal = hasPendingDecisionModels

  const { showDoubleArrowhead } = useNavigation({
    steps,
    activityModels: interactionModel.flatData?.activity_models,
    activityValues: interactionModel.flatData?.activity_values
  })

  const updateActivities = useCallback(
    (activities: Activity[]) => {
      dispatch(interactionModelOperations.updateActivities(activities))
    },
    [dispatch]
  )

  const { executeActionsAfterBlueprints } =
    useBlueprintsAfterExecution(interactionModelData)

  const executeBlueprints = useCallback(
    // TODO: Use this info to run specific actions after the blueprints are executed
    (trigger?: BlueprintTrigger) => {
      if (!interactionModelData) return

      const interactionModelUuid = interactionModelData?.uuid

      const activities = flatValuesToOldActivities(
        interactionModelData.activity_values
      )

      setIsFetchingBlueprints(true)

      const blueprintsRequestActivities = formatBlueprintsRequestActivities(
        activities,
        interactionModelData.activity_models,
        language
      )

      blueprintsPromise.current = interactionApi
        .executeBlueprints({
          interactionModelUuid,
          workflowUuid,
          activities: blueprintsRequestActivities
        })
        .then((response) => {
          if (response.success) {
            const formattedActivities = formatBlueprintsResponseActivities(
              response.data?.activities,
              interactionModelData.activity_models,
              language as AllowedLocales
            )
            updateActivities(formattedActivities)
            executeActionsAfterBlueprints(trigger)
          }
        })
        .catch((error) => {
          if (isFirstBlueprintsRequest && error.status === 503) {
            dispatch({ type: 'api/ERROR', data: { status: error.status } })
            return
          }
          if (error.status === 503) {
            displayUserError({
              title: t('system.unavailable'),
              userMessageFallback: t('system.unavailable-des'),
              error
            })
          }
        })
        .finally(() => {
          setIsFetchingBlueprints(false)
          setIsFirstBlueprintsRequest(false)
          blueprintsPromise.current = null
        })
    },
    [
      dispatch,
      displayUserError,
      executeActionsAfterBlueprints,
      interactionModelData,
      isFirstBlueprintsRequest,
      language,
      t,
      updateActivities,
      workflowUuid
    ]
  )

  const isApplyingDraft =
    draftInteractionUuid && !interactionModel?.isDraftApplied

  useEffect(() => {
    if (
      shouldExecuteInitialBlueprint.current &&
      !isFetchingInteractionModel &&
      !isApplyingDraft &&
      hasAppliedValuesFromProcess &&
      interactionModelData?.uuid
    ) {
      shouldExecuteInitialBlueprint.current = false

      executeBlueprints({ type: 'initial' })
    }
  }, [
    isFetchingInteractionModel,
    interactionModelData?.uuid,
    executeBlueprints,
    isApplyingDraft,
    hasAppliedValuesFromProcess
  ])

  const handleChangeActivity = useCallback(
    (activity: Activity) => {
      if (blueprintsPromise.current) {
        blueprintsPromise.current.then(() => updateActivities([activity]))

        return
      }

      updateActivities([activity])
    },
    [updateActivities]
  )

  useEffect(() => {
    const searchParams = new URLSearchParams(search)

    if (
      !searchParams ||
      !shouldAutofillActivities.current ||
      isFetchingInteractionModel
    )
      return

    shouldAutofillActivities.current = false
    const activities = JSON.parse(searchParams.get('activities') || '{}')

    Object.keys(activities).forEach((key) => {
      const activity = activities[key]
      handleChangeActivity({
        uuid_activity_model: key,
        des: activity?.des || activity,
        complement: activity?.complement
      })
    })
  }, [
    search,
    handleChangeActivity,
    isFetchingInteractionModel,
    shouldAutofillActivities
  ])

  const shouldDisplayError =
    !isFetchingInteractionModel && interactionModelError

  const draftNotFound = isDraftFetched && !draftInteraction
  const isDraftConcluded =
    draftInteraction?.situation === window.activeSituationCode

  const isSplashScreen =
    !shouldDisplayError && (isFetchingInteractionModel || !isLoadedSteps)

  const loading = useAppSelector((state) => state.loading)
  const { active: isLoading, des, complement } = loading

  if (isLoading) {
    const Des = des ? (
      <Text variant='content-emphasis-06'>{des}</Text>
    ) : undefined

    const Complement = complement ? (
      <Text variant='bordertext-04'>{complement}</Text>
    ) : undefined

    return <Loading active des={Des} complement={Complement} />
  }

  if (isSplashScreen) {
    return <Loading active />
  }

  if (draftNotFound) {
    return <DraftNotFoundErrorPage />
  }

  if (isDraftConcluded) {
    return <DraftIsConcludedErrorPage />
  }

  if (shouldDisplayError || draftNotFound) return <GenericErrorPage />

  if (apiState.isError && apiState.status === 503) {
    return (
      <GridContainer>
        <ErrorPage503 onTryAgain={() => window.location.reload()} />
      </GridContainer>
    )
  }

  return (
    <BlueprintsContext.Provider
      value={{ executeBlueprints, isFetchingBlueprints }}
    >
      <ScrollToTop />

      <InteractionInformation
        cards={interactionModelData?.information_cards || []}
        currentStep={steps[currentStepIndex - 1]}
        {...{ currentStepIndex, isInteractionFinished }}
      />

      {!isInteractionFinished && (
        <>
          {!hideInteractionContainer && (
            <GridContainer>
              <DocumentListCard className='my-3'>
                <Unifree
                  informational
                  label={t('interactionmodel')}
                  value={interactionModelData?.des || ''}
                  required
                />
              </DocumentListCard>
            </GridContainer>
          )}

          <ReviewSaveButtonContextProvider>
            <StickyArea>
              <StyledNavigationSlider
                pulseAnimateOnNext
                currentPosition={currentStepIndex}
                first={1}
                total={totalSteps}
                hierarchyLevel='level_2'
                doubleArrowhead={showDoubleArrowhead}
                textForCurrent={t('navigation-slider.text-for-current')}
                textForTotal={
                  showTextForTotal
                    ? t('navigation-slider.text-for-total')
                    : undefined
                }
                afterClick={changeStep}
                heightClass='small'
              />
              <ReviewSaveButton
                submitButton={interactionModelData?.review?.button_label}
                {...{
                  totalSteps,
                  currentStepIndex,
                  changeStep
                }}
              />
            </StickyArea>

            <GridContainer>
              <SelectAndLinkFrameContextProvider>
                <InteractionStep
                  totalSteps={totalSteps}
                  currentStepIndex={currentStepIndex}
                  steps={steps}
                  activityModels={
                    interactionModel.flatData?.activity_models || {}
                  }
                  activityValues={
                    interactionModel.flatData?.activity_values || {}
                  }
                  handleChangeStep={changeStep}
                  handleChangeActivity={handleChangeActivity}
                />
              </SelectAndLinkFrameContextProvider>
            </GridContainer>
          </ReviewSaveButtonContextProvider>
        </>
      )}

      {isInteractionFinished && (
        <GridContainer>
          <Success interactionModel={interactionModel.flatData} />
        </GridContainer>
      )}
    </BlueprintsContext.Provider>
  )
}

export default InteractionContainer
